// Spec 45 component-behavior — cmp_ds_skeleton.
//
// Loading-state placeholder per spec 43 §feat_ds_pattern_loading_state
// AC: "Skeletons match the final layout in shape and spacing". Three
// shape variants:
//
//   * line   — single horizontal bar (text placeholder).
//   * circle — circular blob (avatar placeholder).
//   * block  — rectangular surface (image / card placeholder).
//
// aria-busy='true' + role='status' so screenreaders announce loading
// without reading garbage placeholder text. The shimmer animation
// respects prefers-reduced-motion (CSS-side; the component just
// emits the data-attrs).

import { forwardRef, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export type SkeletonShape = "line" | "circle" | "block";

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  readonly shape?: SkeletonShape;
  /** Width hint — string (e.g., "60%", "120px") or number (px). Defaults to '100%' for line/block, equal-to-height for circle. */
  readonly width?: string | number;
  /** Height hint — same conventions. Defaults to '1em' for line, '40px' for circle/block. */
  readonly height?: string | number;
  /** Accessible label for the loading indicator. Defaults to 'Loading…' which is fine for most cases. */
  readonly "aria-label"?: string;
}

function toCssDim(v: string | number | undefined, fallback: string): string {
  if (v === undefined) return fallback;
  return typeof v === "number" ? `${v}px` : v;
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  ({ shape = "line", width, height, className, style, ...rest }, ref) => {
    const resolvedWidth = toCssDim(width, shape === "circle" ? "40px" : "100%");
    const resolvedHeight = toCssDim(height, shape === "line" ? "1em" : "40px");
    return (
      <div
        ref={ref}
        role="status"
        aria-busy="true"
        aria-label={rest["aria-label"] ?? "Loading…"}
        data-component="skeleton"
        data-shape={shape}
        className={cx("nai-skeleton", className)}
        style={{ width: resolvedWidth, height: resolvedHeight, ...style }}
        {...rest}
      />
    );
  },
);
Skeleton.displayName = "Skeleton";
